// WeightSample v0 PRD §3: ships behind a Crew Mode feature flag that stays
// off for every pilot farm until the capture screen is wired to the shared
// operator-identity mechanism (Charter D-4) and stamps capturedByActorId.
// Like Mortality, WeightSample has no D-3 or O-3 precondition PR — unlike
// Feed, whose flag was gated on three separate merges. Mirrors
// mortalityLogFeatureFlag.ts's shape exactly — flag granularity
// (per-tenant vs per-farm vs per-operator) is an explicit PRD non-goal;
// this is the minimum viable per-farm toggle, flipped manually per farm
// during pilot setup, not a self-serve admin UI.

import { getActiveTenantKey, getTenantScopedKey } from '../../offline/tenant'

const FLAG_BASE_KEY = 'crewModeWeightSampleEnabledFarms'

const hasStorage = (): boolean => typeof globalThis !== 'undefined' && Boolean(globalThis.window?.localStorage)

const readEnabledFarmIds = (tenantKey: string): Set<string> => {
  if (!hasStorage()) return new Set()
  try {
    const raw = globalThis.window?.localStorage?.getItem(getTenantScopedKey(FLAG_BASE_KEY, tenantKey))
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? new Set(parsed.filter((value): value is string => typeof value === 'string')) : new Set()
  } catch {
    return new Set()
  }
}

const writeEnabledFarmIds = (tenantKey: string, farmIds: Set<string>): void => {
  if (!hasStorage()) return
  try {
    globalThis.window?.localStorage?.setItem(
      getTenantScopedKey(FLAG_BASE_KEY, tenantKey),
      JSON.stringify(Array.from(farmIds))
    )
  } catch {
    // ignore
  }
}

export const isWeightSampleCrewModeEnabled = (farmId: string, tenantKey = getActiveTenantKey()): boolean => {
  if (!farmId) return false
  return readEnabledFarmIds(tenantKey).has(farmId)
}

export const setWeightSampleCrewModeEnabled = (
  farmId: string,
  enabled: boolean,
  tenantKey = getActiveTenantKey()
): void => {
  if (!farmId) return
  const enabledFarmIds = readEnabledFarmIds(tenantKey)
  if (enabled) {
    enabledFarmIds.add(farmId)
  } else {
    enabledFarmIds.delete(farmId)
  }
  writeEnabledFarmIds(tenantKey, enabledFarmIds)
}
